/**
 * @tokenstreaming/shared — Utility functions.
 * Pure functions, no side effects, no external deps.
 */

/**
 * Safe BigInt multiplication with floor division for streaming rate computation.
 * Returns floor(amount * elapsedMs / 1000) — the cumulative streamed amount.
 */
export function floorMul(ratePerSecAtomic: bigint, elapsedMs: number): bigint {
  if (elapsedMs <= 0) return 0n;
  return (ratePerSecAtomic * BigInt(Math.floor(elapsedMs))) / 1000n;
}

/**
 * Format a token amount from atomic units to human-readable string.
 * E.g. formatTokenAmount(1500000000000000000n, 18) → "1.5"
 */
export function formatTokenAmount(amount: bigint, decimals: number): string {
  if (decimals === 0) return amount.toString();

  const divisor = 10n ** BigInt(decimals);
  const integerPart = amount / divisor;
  const fractionalPart = amount % divisor;

  if (fractionalPart === 0n) {
    return addThousandSeparators(integerPart.toString());
  }

  const fracStr = fractionalPart.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${addThousandSeparators(integerPart.toString())}.${fracStr}`;
}

/**
 * Parse a human-readable token amount string to atomic bigint.
 * E.g. parseTokenAmount("1.5", 18) → 1500000000000000000n
 * Throws on invalid input.
 */
export function parseTokenAmount(input: string, decimals: number): bigint {
  const trimmed = input.trim().replace(/,/g, '');
  if (!/^[0-9]*\.?[0-9]*$/.test(trimmed) || trimmed === '' || trimmed === '.') {
    throw new Error(`Invalid token amount: "${input}"`);
  }

  const parts = trimmed.split('.');
  const intPart = parts[0] ?? '0';
  const fracPart = (parts[1] ?? '').slice(0, decimals).padEnd(decimals, '0');

  const combined = intPart + fracPart;
  return BigInt(combined);
}

/**
 * Shorten an address for display: 0x742d...bD28
 */
export function shortenAddress(address: string, chars = 4): string {
  if (address.length <= chars * 2 + 4) return address;
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}

/**
 * Build a block explorer URL for a transaction.
 */
export function explorerTxUrl(explorerBase: string, txHash: string): string {
  return `${explorerBase}/tx/${txHash}`;
}

/**
 * Build a block explorer URL for an address.
 */
export function explorerAddressUrl(explorerBase: string, address: string): string {
  return `${explorerBase}/address/${address}`;
}

/**
 * Sleep for a given duration.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff.
 * Throws the last error if all attempts fail.
 */
export async function retry<T>(
  fn: () => Promise<T>,
  attempts: number = 3,
  baseDelayMs: number = 1000
): Promise<T> {
  let lastError: Error | undefined;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (i < attempts - 1) {
        const delay = baseDelayMs * Math.pow(2, i);
        await sleep(delay);
      }
    }
  }
  throw lastError;
}

/**
 * Validate an Ethereum address (0x + 40 hex chars).
 */
export function isValidAddress(address: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(address);
}

/**
 * Convert an address to checksummed format (EIP-55).
 * Simplified version — for full compliance use ethers.getAddress().
 */
export function normalizeAddress(address: string): string {
  return address.toLowerCase();
}

/**
 * Format elapsed time in human-readable form.
 */
export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function addThousandSeparators(num: string): string {
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
